import { useCallback, useMemo, useRef, useState } from "react";
import { ALL_GRADES } from "@/lib/fieldTypeCatalog";
import type { BuilderSchemaResponse, FieldTemplate } from "@/lib/wartungen";
import {
  type BuilderField,
  type BuilderScope,
  buildExistingKeySet,
  createFieldFromTemplate,
  fieldToSavePayload,
  insertFieldAsSingleRow,
  normalizeField,
  normalizeRowLayoutForScope,
  placeFieldBesideAnchor,
  semanticSortOrderByKey,
} from "./fieldModel";
import { normalizeBehaviorForSave } from "./answerRules";
import { materializeDefectTrigger, type DefectTriggerSource } from "./defectTrigger";

export interface LoadSchemaResult {
  /** Felder, deren Mangel-Regel beim Laden automatisch vervollständigt wurde. */
  autoCompleted: { uid: string; label: string; source: DefectTriggerSource }[];
}

/** Save-Payload eines Feldes inkl. bereinigter Antwortautomatik (unvollständige Regeln entfernen). */
function fieldToNormalizedSavePayload(
  field: BuilderField,
  formId: string,
  sortOrder: number,
  allFields: BuilderField[],
): Record<string, unknown> {
  const payload = fieldToSavePayload(field, formId, sortOrder);
  payload.behavior_override_json = normalizeBehaviorForSave(payload.behavior_override_json, allFields);
  return payload;
}

export interface GradeConfig {
  enabled_grades: number[];
}

export interface DropTarget {
  scope: BuilderScope;
  kind: "above" | "below" | "side" | "end";
  anchorUid?: string;
  /** Einfügeposition in der Zielzeile (0 = links, …). */
  insertCol?: number;
  side?: "left" | "right";
}

function signatureOf(fields: BuilderField[], grade: GradeConfig, formId: string): string {
  const order = semanticSortOrderByKey(fields);
  const payload = fields
    .map((f) => fieldToNormalizedSavePayload(f, formId, order.get(f.field_key) ?? -1, fields))
    .sort((a, b) => Number(a.sort_order) - Number(b.sort_order));
  return JSON.stringify({ payload, grade: [...grade.enabled_grades].sort((x, y) => x - y) });
}

export function useBuilder(formId: string) {
  const [fields, setFields] = useState<BuilderField[]>([]);
  const [grade, setGrade] = useState<GradeConfig>({ enabled_grades: [...ALL_GRADES] });
  const [editingUid, setEditingUid] = useState<string>("");
  const [selectedUid, setSelectedUid] = useState<string>("");

  // Snapshots zum Dirty-Vergleich & Reset.
  const loadedSignatureRef = useRef<string>("");
  const originalKeyByUidRef = useRef<Map<string, string>>(new Map());
  const originalByUidRef = useRef<Map<string, BuilderField>>(new Map());
  const deletedKeysRef = useRef<Set<string>>(new Set());

  const [, forceTick] = useState(0);
  const rerender = useCallback(() => forceTick((t) => t + 1), []);

  const loadFromSchema = useCallback(
    (schema: BuilderSchemaResponse): LoadSchemaResult => {
      const formName = String(schema.form_name || schema.name || "");
      const raws = Array.isArray(schema.fields) ? schema.fields : [];
      // „pristine“ = exakt der DB-Stand (Basis für Dirty-Vergleich und Zurücksetzen).
      const pristine = raws.map((r) => normalizeField(r, formId, formName));
      const gradeCfg: GradeConfig = {
        enabled_grades:
          Array.isArray(schema.grade_config_json?.enabled_grades) &&
          schema.grade_config_json!.enabled_grades!.length
            ? [...schema.grade_config_json!.enabled_grades!]
            : [...ALL_GRADES],
      };
      const origKey = new Map<string, string>();
      const origField = new Map<string, BuilderField>();
      for (const f of pristine) {
        origKey.set(f.uid, f.field_key);
        origField.set(f.uid, structuredClone(f));
      }
      originalKeyByUidRef.current = origKey;
      originalByUidRef.current = origField;
      deletedKeysRef.current = new Set();
      loadedSignatureRef.current = signatureOf(pristine, gradeCfg, formId);

      // Arbeitskopie: Mangel-Regeln vervollständigen, die der Editor als Standard anzeigt
      // (Ja/Nein → „Ja“ = Mangel) oder die der Server bereits aufgelöst hat (Template-Default).
      // Dadurch wird der Zustand speicherbar und die Publish-Prüfung schlägt nicht mehr fehl,
      // obwohl das Feld „ausgefüllt“ aussah.
      const autoCompleted: LoadSchemaResult["autoCompleted"] = [];
      const working = pristine.map((f, index) => {
        const clone = structuredClone(f);
        const source = materializeDefectTrigger(clone, raws[index]?.defect_trigger);
        if (source) autoCompleted.push({ uid: clone.uid, label: clone.label || clone.field_key, source });
        return clone;
      });

      setFields(working);
      setGrade(gradeCfg);
      setEditingUid("");
      setSelectedUid("");
      return { autoCompleted };
    },
    [formId],
  );

  const commit = useCallback((next: BuilderField[]) => {
    setFields(next);
  }, []);

  const mutateField = useCallback(
    (uid: string, patch: Partial<BuilderField> | ((f: BuilderField) => void)) => {
      setFields((prev) =>
        prev.map((f) => {
          if (f.uid !== uid) return f;
          const clone = { ...f };
          if (typeof patch === "function") patch(clone);
          else Object.assign(clone, patch);
          return clone;
        }),
      );
    },
    [],
  );

  /** Tiefen-Mutationen (verschachtelte JSON) – ersetzt Feld komplett. */
  const replaceField = useCallback((next: BuilderField) => {
    setFields((prev) => prev.map((f) => (f.uid === next.uid ? next : f)));
  }, []);

  const deleteField = useCallback((uid: string) => {
    setFields((prev) => {
      const target = prev.find((f) => f.uid === uid);
      if (target) {
        const origKey = originalKeyByUidRef.current.get(uid);
        if (origKey) deletedKeysRef.current.add(origKey);
      }
      const next = prev.filter((f) => f.uid !== uid);
      const scope = target?.scope;
      if (scope) normalizeRowLayoutForScope(next, scope);
      return [...next];
    });
    setEditingUid((cur) => (cur === uid ? "" : cur));
  }, []);

  const resetField = useCallback((uid: string) => {
    const original = originalByUidRef.current.get(uid);
    if (!original) return;
    setFields((prev) => prev.map((f) => (f.uid === uid ? structuredClone(original) : f)));
  }, []);

  /** Drop eines Palette-Templates auf einen Canvas. */
  const dropTemplate = useCallback(
    (template: FieldTemplate | null, scope: BuilderScope, target: DropTarget | null) => {
      setFields((prev) => {
        const next = [...prev];
        const created = createFieldFromTemplate(template, scope, buildExistingKeySet(next));
        // Vorlagen mit Rolle „Mangel“ sofort mit gültiger Mangel-Regel versehen.
        materializeDefectTrigger(created);
        next.push(created);
        applyPlacement(next, created, scope, target);
        normalizeRowLayoutForScope(next, scope);
        setSelectedUid(created.uid);
        return next;
      });
    },
    [],
  );

  /** Bestehendes Feld per DnD neu platzieren. */
  const moveField = useCallback((uid: string, scope: BuilderScope, target: DropTarget | null) => {
    setFields((prev) => {
      const next = prev.map((f) => ({ ...f }));
      const moving = next.find((f) => f.uid === uid);
      if (!moving) return prev;
      applyPlacement(next, moving, scope, target);
      normalizeRowLayoutForScope(next, scope);
      return next;
    });
  }, []);

  const toggleGrade = useCallback((g: number) => {
    setGrade((prev) => {
      const set = new Set(prev.enabled_grades);
      if (set.has(g)) set.delete(g);
      else set.add(g);
      return { enabled_grades: [...set].sort((a, b) => a - b) };
    });
  }, []);

  const currentSignature = useMemo(
    () => signatureOf(fields, grade, formId),
    [fields, grade, formId],
  );
  const isDirty = currentSignature !== loadedSignatureRef.current;

  const dirtyUids = useMemo(() => {
    const order = semanticSortOrderByKey(fields);
    const dirty = new Set<string>();
    for (const f of fields) {
      const original = originalByUidRef.current.get(f.uid);
      if (!original) {
        dirty.add(f.uid);
        continue;
      }
      const currentPayload = fieldToNormalizedSavePayload(f, formId, order.get(f.field_key) ?? -1, fields);
      const originalPayload = fieldToNormalizedSavePayload(original, formId, order.get(original.field_key) ?? -1, fields);
      if (JSON.stringify(currentPayload) !== JSON.stringify(originalPayload)) {
        dirty.add(f.uid);
      }
    }
    return dirty;
  }, [fields, formId]);

  const buildSavePayload = useCallback(() => {
    const order = semanticSortOrderByKey(fields);
    const fieldsPayload = fields.map((f) =>
      fieldToNormalizedSavePayload(f, formId, order.get(f.field_key) ?? -1, fields),
    );
    const renamed: { from: string; to: string }[] = [];
    for (const f of fields) {
      const orig = originalKeyByUidRef.current.get(f.uid);
      if (orig && orig !== f.field_key) renamed.push({ from: orig, to: f.field_key });
    }
    return {
      fields: fieldsPayload,
      deleted_field_keys: Array.from(deletedKeysRef.current),
      renamed_field_keys: renamed,
      grade_config_json: { enabled_grades: [...grade.enabled_grades].sort((a, b) => a - b) },
    };
  }, [fields, grade, formId]);

  return {
    fields,
    setFields,
    grade,
    editingUid,
    setEditingUid,
    selectedUid,
    setSelectedUid,
    isDirty,
    dirtyUids,
    loadFromSchema,
    mutateField,
    replaceField,
    deleteField,
    resetField,
    dropTemplate,
    moveField,
    toggleGrade,
    buildSavePayload,
    commit,
    rerender,
  };
}

/** Platzierung (oben/unten/seitlich/ende) auf ein Feld anwenden. */
function applyPlacement(
  all: BuilderField[],
  field: BuilderField,
  scope: BuilderScope,
  target: DropTarget | null,
): void {
  const scoped = all.filter((f) => f.scope === scope && f !== field);
  if (!target || target.kind === "end" || !target.anchorUid) {
    const maxRow = scoped.reduce((m, f) => Math.max(m, f.layout_row), -1);
    insertFieldAsSingleRow(all, field, scope, maxRow + 1);
    return;
  }
  const anchor = all.find((f) => f.uid === target.anchorUid);
  if (!anchor) {
    const maxRow = scoped.reduce((m, f) => Math.max(m, f.layout_row), -1);
    insertFieldAsSingleRow(all, field, scope, maxRow + 1);
    return;
  }
  if (target.kind === "above") {
    insertFieldAsSingleRow(all, field, scope, anchor.layout_row);
  } else if (target.kind === "below") {
    insertFieldAsSingleRow(all, field, scope, anchor.layout_row + 1);
  } else {
    const insertCol =
      Number.isInteger(target.insertCol)
        ? target.insertCol!
        : target.side === "right"
          ? anchor.layout_col + 1
          : anchor.layout_col;
    placeFieldBesideAnchor(all, field, scope, anchor, insertCol);
  }
}
